/**
 * Generated by orval 🍺
 * Do not edit manually.
 * TrainLoop Platform V2 API
 * Model evaluation workbench API
 * OpenAPI spec version: 0.0.0
 */
import { useQuery } from "@tanstack/react-query";
import type {
  DataTag,
  DefinedInitialDataOptions,
  DefinedUseQueryResult,
  QueryClient,
  QueryFunction,
  QueryKey,
  UndefinedInitialDataOptions,
  UseQueryOptions,
  UseQueryResult,
} from "@tanstack/react-query";

import type {
  FailureRecordResponse,
  HTTPValidationError,
  ReadFailureRecordApiFailureAnalysisRecordsRecordIdGetParams,
  ReadFailureRecordTranscriptApiFailureAnalysisRecordsRecordIdTranscriptsNameGetParams,
  TrainingAgentFile,
} from "../model";

import { orvalFetch } from "../../client.ts";
import type { ErrorType } from "../../client.ts";

type SecondParameter<T extends (...args: never) => unknown> = Parameters<T>[1];

const withQueryKey = <T extends object, K>(
  query: T,
  queryKey: K,
): T & { queryKey: K } => {
  const result = { queryKey } as T & { queryKey: K };
  for (const key of Object.keys(query)) {
    // The explicit queryKey always wins, matching the previous
    // `{ ...query, queryKey }` spread where it was set last.
    if (key === "queryKey") continue;
    Object.defineProperty(result, key, {
      enumerable: true,
      configurable: true,
      get: () => (query as Record<string, unknown>)[key],
    });
  }
  return result;
};

export type readFailureRecordApiFailureAnalysisRecordsRecordIdGetResponse200 = {
  data: FailureRecordResponse;
  status: 200;
};

export type readFailureRecordApiFailureAnalysisRecordsRecordIdGetResponse422 = {
  data: HTTPValidationError;
  status: 422;
};

export type readFailureRecordApiFailureAnalysisRecordsRecordIdGetResponseSuccess =
  readFailureRecordApiFailureAnalysisRecordsRecordIdGetResponse200 & {
    headers: Headers;
  };
export type readFailureRecordApiFailureAnalysisRecordsRecordIdGetResponseError =
  readFailureRecordApiFailureAnalysisRecordsRecordIdGetResponse422 & {
    headers: Headers;
  };

export type readFailureRecordApiFailureAnalysisRecordsRecordIdGetResponse =
  | readFailureRecordApiFailureAnalysisRecordsRecordIdGetResponseSuccess
  | readFailureRecordApiFailureAnalysisRecordsRecordIdGetResponseError;

export const getReadFailureRecordApiFailureAnalysisRecordsRecordIdGetUrl = (
  recordId: string,
  params?: ReadFailureRecordApiFailureAnalysisRecordsRecordIdGetParams,
) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {
    if (value !== undefined) {
      normalizedParams.append(key, value === null ? "null" : String(value));
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0
    ? `/api/failure-analysis/records/${recordId}?${stringifiedParams}`
    : `/api/failure-analysis/records/${recordId}`;
};

/**
 * One failure record: the analysis, each run's turns and where the
 * record stands with task generation. Opening it is recorded in the
 * team's audit log (TRA-1626).
 * @summary Read Failure Record
 */
export const readFailureRecordApiFailureAnalysisRecordsRecordIdGet = async (
  recordId: string,
  params?: ReadFailureRecordApiFailureAnalysisRecordsRecordIdGetParams,
  options?: Parameters<typeof orvalFetch>[1],
): Promise<readFailureRecordApiFailureAnalysisRecordsRecordIdGetResponse> => {
  return orvalFetch<readFailureRecordApiFailureAnalysisRecordsRecordIdGetResponse>(
    getReadFailureRecordApiFailureAnalysisRecordsRecordIdGetUrl(
      recordId,
      params,
    ),
    {
      ...options,
      method: "GET",
    },
  );
};

export const getReadFailureRecordApiFailureAnalysisRecordsRecordIdGetQueryKey =
  (
    recordId: string,
    params?: ReadFailureRecordApiFailureAnalysisRecordsRecordIdGetParams,
  ) => {
    return [
      `/api/failure-analysis/records/${recordId}`,
      ...(params ? [params] : []),
    ] as const;
  };

export const getReadFailureRecordApiFailureAnalysisRecordsRecordIdGetQueryOptions =
  <
    TData = Awaited<
      ReturnType<typeof readFailureRecordApiFailureAnalysisRecordsRecordIdGet>
    >,
    TError = ErrorType<HTTPValidationError>,
  >(
    recordId: string,
    params?: ReadFailureRecordApiFailureAnalysisRecordsRecordIdGetParams,
    options?: {
      query?: Partial<
        UseQueryOptions<
          Awaited<
            ReturnType<
              typeof readFailureRecordApiFailureAnalysisRecordsRecordIdGet
            >
          >,
          TError,
          TData
        >
      >;
      request?: SecondParameter<typeof orvalFetch>;
    },
  ) => {
    const { query: queryOptions, request: requestOptions } = options ?? {};

    const queryKey =
      queryOptions?.queryKey ??
      getReadFailureRecordApiFailureAnalysisRecordsRecordIdGetQueryKey(
        recordId,
        params,
      );

    const queryFn: QueryFunction<
      Awaited<
        ReturnType<typeof readFailureRecordApiFailureAnalysisRecordsRecordIdGet>
      >
    > = ({ signal }) =>
      readFailureRecordApiFailureAnalysisRecordsRecordIdGet(recordId, params, {
        signal,
        ...requestOptions,
      });

    return {
      queryKey,
      queryFn,
      enabled: recordId !== null && recordId !== undefined,
      ...queryOptions,
    } as UseQueryOptions<
      Awaited<
        ReturnType<typeof readFailureRecordApiFailureAnalysisRecordsRecordIdGet>
      >,
      TError,
      TData
    > & { queryKey: DataTag<QueryKey, TData, TError> };
  };

export type ReadFailureRecordApiFailureAnalysisRecordsRecordIdGetQueryResult =
  NonNullable<
    Awaited<
      ReturnType<typeof readFailureRecordApiFailureAnalysisRecordsRecordIdGet>
    >
  >;
export type ReadFailureRecordApiFailureAnalysisRecordsRecordIdGetQueryError =
  ErrorType<HTTPValidationError>;

export function useReadFailureRecordApiFailureAnalysisRecordsRecordIdGet<
  TData = Awaited<
    ReturnType<typeof readFailureRecordApiFailureAnalysisRecordsRecordIdGet>
  >,
  TError = ErrorType<HTTPValidationError>,
>(
  recordId: string,
  params:
    | undefined
    | ReadFailureRecordApiFailureAnalysisRecordsRecordIdGetParams,
  options: {
    query: Partial<
      UseQueryOptions<
        Awaited<
          ReturnType<
            typeof readFailureRecordApiFailureAnalysisRecordsRecordIdGet
          >
        >,
        TError,
        TData
      >
    > &
      Pick<
        DefinedInitialDataOptions<
          Awaited<
            ReturnType<
              typeof readFailureRecordApiFailureAnalysisRecordsRecordIdGet
            >
          >,
          TError,
          Awaited<
            ReturnType<
              typeof readFailureRecordApiFailureAnalysisRecordsRecordIdGet
            >
          >
        >,
        "initialData"
      >;
    request?: SecondParameter<typeof orvalFetch>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function useReadFailureRecordApiFailureAnalysisRecordsRecordIdGet<
  TData = Awaited<
    ReturnType<typeof readFailureRecordApiFailureAnalysisRecordsRecordIdGet>
  >,
  TError = ErrorType<HTTPValidationError>,
>(
  recordId: string,
  params?: ReadFailureRecordApiFailureAnalysisRecordsRecordIdGetParams,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<
          ReturnType<
            typeof readFailureRecordApiFailureAnalysisRecordsRecordIdGet
          >
        >,
        TError,
        TData
      >
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<
            ReturnType<
              typeof readFailureRecordApiFailureAnalysisRecordsRecordIdGet
            >
          >,
          TError,
          Awaited<
            ReturnType<
              typeof readFailureRecordApiFailureAnalysisRecordsRecordIdGet
            >
          >
        >,
        "initialData"
      >;
    request?: SecondParameter<typeof orvalFetch>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function useReadFailureRecordApiFailureAnalysisRecordsRecordIdGet<
  TData = Awaited<
    ReturnType<typeof readFailureRecordApiFailureAnalysisRecordsRecordIdGet>
  >,
  TError = ErrorType<HTTPValidationError>,
>(
  recordId: string,
  params?: ReadFailureRecordApiFailureAnalysisRecordsRecordIdGetParams,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<
          ReturnType<
            typeof readFailureRecordApiFailureAnalysisRecordsRecordIdGet
          >
        >,
        TError,
        TData
      >
    >;
    request?: SecondParameter<typeof orvalFetch>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
/**
 * @summary Read Failure Record
 */

export function useReadFailureRecordApiFailureAnalysisRecordsRecordIdGet<
  TData = Awaited<
    ReturnType<typeof readFailureRecordApiFailureAnalysisRecordsRecordIdGet>
  >,
  TError = ErrorType<HTTPValidationError>,
>(
  recordId: string,
  params?: ReadFailureRecordApiFailureAnalysisRecordsRecordIdGetParams,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<
          ReturnType<
            typeof readFailureRecordApiFailureAnalysisRecordsRecordIdGet
          >
        >,
        TError,
        TData
      >
    >;
    request?: SecondParameter<typeof orvalFetch>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
} {
  const queryOptions =
    getReadFailureRecordApiFailureAnalysisRecordsRecordIdGetQueryOptions(
      recordId,
      params,
      options,
    );

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<
    TData,
    TError
  > & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}

export type readFailureRecordTranscriptApiFailureAnalysisRecordsRecordIdTranscriptsNameGetResponse200 =
  {
    data: TrainingAgentFile;
    status: 200;
  };

export type readFailureRecordTranscriptApiFailureAnalysisRecordsRecordIdTranscriptsNameGetResponse422 =
  {
    data: HTTPValidationError;
    status: 422;
  };

export type readFailureRecordTranscriptApiFailureAnalysisRecordsRecordIdTranscriptsNameGetResponseSuccess =
  readFailureRecordTranscriptApiFailureAnalysisRecordsRecordIdTranscriptsNameGetResponse200 & {
    headers: Headers;
  };
export type readFailureRecordTranscriptApiFailureAnalysisRecordsRecordIdTranscriptsNameGetResponseError =
  readFailureRecordTranscriptApiFailureAnalysisRecordsRecordIdTranscriptsNameGetResponse422 & {
    headers: Headers;
  };

export type readFailureRecordTranscriptApiFailureAnalysisRecordsRecordIdTranscriptsNameGetResponse =

    | readFailureRecordTranscriptApiFailureAnalysisRecordsRecordIdTranscriptsNameGetResponseSuccess
    | readFailureRecordTranscriptApiFailureAnalysisRecordsRecordIdTranscriptsNameGetResponseError;

export const getReadFailureRecordTranscriptApiFailureAnalysisRecordsRecordIdTranscriptsNameGetUrl =
  (
    recordId: string,
    name: string,
    params: ReadFailureRecordTranscriptApiFailureAnalysisRecordsRecordIdTranscriptsNameGetParams,
  ) => {
    const normalizedParams = new URLSearchParams();

    Object.entries(params || {}).forEach(([key, value]) => {
      if (value !== undefined) {
        normalizedParams.append(key, value === null ? "null" : String(value));
      }
    });

    const stringifiedParams = normalizedParams.toString();

    return stringifiedParams.length > 0
      ? `/api/failure-analysis/records/${recordId}/transcripts/${name}?${stringifiedParams}`
      : `/api/failure-analysis/records/${recordId}/transcripts/${name}`;
  };

/**
 * One transcript of the record, whole, as text. Opening it is
 * recorded in the team's audit log (TRA-1626), by name, never by text.
 * @summary Read Failure Record Transcript
 */
export const readFailureRecordTranscriptApiFailureAnalysisRecordsRecordIdTranscriptsNameGet =
  async (
    recordId: string,
    name: string,
    params: ReadFailureRecordTranscriptApiFailureAnalysisRecordsRecordIdTranscriptsNameGetParams,
    options?: Parameters<typeof orvalFetch>[1],
  ): Promise<readFailureRecordTranscriptApiFailureAnalysisRecordsRecordIdTranscriptsNameGetResponse> => {
    return orvalFetch<readFailureRecordTranscriptApiFailureAnalysisRecordsRecordIdTranscriptsNameGetResponse>(
      getReadFailureRecordTranscriptApiFailureAnalysisRecordsRecordIdTranscriptsNameGetUrl(
        recordId,
        name,
        params,
      ),
      {
        ...options,
        method: "GET",
      },
    );
  };

export const getReadFailureRecordTranscriptApiFailureAnalysisRecordsRecordIdTranscriptsNameGetQueryKey =
  (
    recordId: string,
    name: string,
    params?: ReadFailureRecordTranscriptApiFailureAnalysisRecordsRecordIdTranscriptsNameGetParams,
  ) => {
    return [
      `/api/failure-analysis/records/${recordId}/transcripts/${name}`,
      ...(params ? [params] : []),
    ] as const;
  };

export const getReadFailureRecordTranscriptApiFailureAnalysisRecordsRecordIdTranscriptsNameGetQueryOptions =
  <
    TData = Awaited<
      ReturnType<
        typeof readFailureRecordTranscriptApiFailureAnalysisRecordsRecordIdTranscriptsNameGet
      >
    >,
    TError = ErrorType<HTTPValidationError>,
  >(
    recordId: string,
    name: string,
    params: ReadFailureRecordTranscriptApiFailureAnalysisRecordsRecordIdTranscriptsNameGetParams,
    options?: {
      query?: Partial<
        UseQueryOptions<
          Awaited<
            ReturnType<
              typeof readFailureRecordTranscriptApiFailureAnalysisRecordsRecordIdTranscriptsNameGet
            >
          >,
          TError,
          TData
        >
      >;
      request?: SecondParameter<typeof orvalFetch>;
    },
  ) => {
    const { query: queryOptions, request: requestOptions } = options ?? {};

    const queryKey =
      queryOptions?.queryKey ??
      getReadFailureRecordTranscriptApiFailureAnalysisRecordsRecordIdTranscriptsNameGetQueryKey(
        recordId,
        name,
        params,
      );

    const queryFn: QueryFunction<
      Awaited<
        ReturnType<
          typeof readFailureRecordTranscriptApiFailureAnalysisRecordsRecordIdTranscriptsNameGet
        >
      >
    > = ({ signal }) =>
      readFailureRecordTranscriptApiFailureAnalysisRecordsRecordIdTranscriptsNameGet(
        recordId,
        name,
        params,
        { signal, ...requestOptions },
      );

    return {
      queryKey,
      queryFn,
      enabled:
        recordId !== null &&
        recordId !== undefined &&
        name !== null &&
        name !== undefined,
      ...queryOptions,
    } as UseQueryOptions<
      Awaited<
        ReturnType<
          typeof readFailureRecordTranscriptApiFailureAnalysisRecordsRecordIdTranscriptsNameGet
        >
      >,
      TError,
      TData
    > & { queryKey: DataTag<QueryKey, TData, TError> };
  };

export type ReadFailureRecordTranscriptApiFailureAnalysisRecordsRecordIdTranscriptsNameGetQueryResult =
  NonNullable<
    Awaited<
      ReturnType<
        typeof readFailureRecordTranscriptApiFailureAnalysisRecordsRecordIdTranscriptsNameGet
      >
    >
  >;
export type ReadFailureRecordTranscriptApiFailureAnalysisRecordsRecordIdTranscriptsNameGetQueryError =
  ErrorType<HTTPValidationError>;

export function useReadFailureRecordTranscriptApiFailureAnalysisRecordsRecordIdTranscriptsNameGet<
  TData = Awaited<
    ReturnType<
      typeof readFailureRecordTranscriptApiFailureAnalysisRecordsRecordIdTranscriptsNameGet
    >
  >,
  TError = ErrorType<HTTPValidationError>,
>(
  recordId: string,
  name: string,
  params: ReadFailureRecordTranscriptApiFailureAnalysisRecordsRecordIdTranscriptsNameGetParams,
  options: {
    query: Partial<
      UseQueryOptions<
        Awaited<
          ReturnType<
            typeof readFailureRecordTranscriptApiFailureAnalysisRecordsRecordIdTranscriptsNameGet
          >
        >,
        TError,
        TData
      >
    > &
      Pick<
        DefinedInitialDataOptions<
          Awaited<
            ReturnType<
              typeof readFailureRecordTranscriptApiFailureAnalysisRecordsRecordIdTranscriptsNameGet
            >
          >,
          TError,
          Awaited<
            ReturnType<
              typeof readFailureRecordTranscriptApiFailureAnalysisRecordsRecordIdTranscriptsNameGet
            >
          >
        >,
        "initialData"
      >;
    request?: SecondParameter<typeof orvalFetch>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function useReadFailureRecordTranscriptApiFailureAnalysisRecordsRecordIdTranscriptsNameGet<
  TData = Awaited<
    ReturnType<
      typeof readFailureRecordTranscriptApiFailureAnalysisRecordsRecordIdTranscriptsNameGet
    >
  >,
  TError = ErrorType<HTTPValidationError>,
>(
  recordId: string,
  name: string,
  params: ReadFailureRecordTranscriptApiFailureAnalysisRecordsRecordIdTranscriptsNameGetParams,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<
          ReturnType<
            typeof readFailureRecordTranscriptApiFailureAnalysisRecordsRecordIdTranscriptsNameGet
          >
        >,
        TError,
        TData
      >
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<
            ReturnType<
              typeof readFailureRecordTranscriptApiFailureAnalysisRecordsRecordIdTranscriptsNameGet
            >
          >,
          TError,
          Awaited<
            ReturnType<
              typeof readFailureRecordTranscriptApiFailureAnalysisRecordsRecordIdTranscriptsNameGet
            >
          >
        >,
        "initialData"
      >;
    request?: SecondParameter<typeof orvalFetch>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function useReadFailureRecordTranscriptApiFailureAnalysisRecordsRecordIdTranscriptsNameGet<
  TData = Awaited<
    ReturnType<
      typeof readFailureRecordTranscriptApiFailureAnalysisRecordsRecordIdTranscriptsNameGet
    >
  >,
  TError = ErrorType<HTTPValidationError>,
>(
  recordId: string,
  name: string,
  params: ReadFailureRecordTranscriptApiFailureAnalysisRecordsRecordIdTranscriptsNameGetParams,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<
          ReturnType<
            typeof readFailureRecordTranscriptApiFailureAnalysisRecordsRecordIdTranscriptsNameGet
          >
        >,
        TError,
        TData
      >
    >;
    request?: SecondParameter<typeof orvalFetch>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
/**
 * @summary Read Failure Record Transcript
 */

export function useReadFailureRecordTranscriptApiFailureAnalysisRecordsRecordIdTranscriptsNameGet<
  TData = Awaited<
    ReturnType<
      typeof readFailureRecordTranscriptApiFailureAnalysisRecordsRecordIdTranscriptsNameGet
    >
  >,
  TError = ErrorType<HTTPValidationError>,
>(
  recordId: string,
  name: string,
  params: ReadFailureRecordTranscriptApiFailureAnalysisRecordsRecordIdTranscriptsNameGetParams,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<
          ReturnType<
            typeof readFailureRecordTranscriptApiFailureAnalysisRecordsRecordIdTranscriptsNameGet
          >
        >,
        TError,
        TData
      >
    >;
    request?: SecondParameter<typeof orvalFetch>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
} {
  const queryOptions =
    getReadFailureRecordTranscriptApiFailureAnalysisRecordsRecordIdTranscriptsNameGetQueryOptions(
      recordId,
      name,
      params,
      options,
    );

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<
    TData,
    TError
  > & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}
